import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from "@stripe/react-stripe-js";
import type { Stripe } from "@stripe/stripe-js";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertCircle, ArrowLeft, ArrowRight, Loader2 } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { activateOrganization, confirmSetupIntent } from "@/api/billing";
import { SkeletonRow } from "@/components/common/skeleton-row";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { useDarkMode } from "@/hooks/useDarkMode";
import { queryKeys } from "@/lib/query-keys";
import { getStripe } from "@/lib/stripe";
import { useOrganizationStore } from "@/store/organization";
import { ApiRequestError } from "@/utils/fetch";
import { applyActivatedOrganization } from "../lib/apply-activated-organization";
import {
  paymentErrorMessage,
  stripeSetupErrorMessage,
} from "../lib/payment-errors";
import { resetSetupIntentFormKey } from "../lib/setup-intent-form-key";
import { setupIntentQueryOptions } from "../lib/setup-intent-query";

const ACTIVATION_FAILURE_MESSAGE =
  "Your card was saved, but we couldn't activate your account. Please try again.";

const hasBillingError = (
  error: unknown,
  status: number,
  code: string,
): error is ApiRequestError => {
  if (!(error instanceof ApiRequestError) || error.status !== status) {
    return false;
  }
  const body = error.body as { error?: string } | null;
  return body?.error === code;
};

const activationErrorMessage = (error: unknown): string =>
  hasBillingError(error, 402, "payment_method_required") ||
  hasBillingError(error, 503, "billing_provider_unavailable")
    ? paymentErrorMessage(error)
    : ACTIVATION_FAILURE_MESSAGE;

/**
 * NUX payment step: collects a card into the org's Stripe wallet via a
 * SetupIntent, confirms it server-side (pins the default payment method), then
 * activates the org. Activation writes the store; the container's gates
 * observe `account_status === "active"` and navigate — this component never
 * navigates itself.
 *
 * @param initialErrorMessage - Recovery guidance from a failed activation.
 * @param onBack - Returns to pricing before the SetupIntent is confirmed.
 */
export const PaymentStep = ({
  onBack,
  initialErrorMessage = null,
}: {
  onBack: () => void;
  initialErrorMessage?: string | null;
}) => {
  const queryClient = useQueryClient();
  const organizationId = useOrganizationStore((s) => s.organization?.id);
  const [stripe, setStripe] = useState<Stripe | null | undefined>(undefined);
  const [paymentSessionError, setPaymentSessionError] = useState<string | null>(
    initialErrorMessage,
  );
  const [isPaymentBusy, setIsPaymentBusy] = useState(false);
  const [locallyConfirmedSetupIntentId, setLocallyConfirmedSetupIntentId] =
    useState<string | null>(null);
  const setupRequestInFlight = useRef(false);

  useEffect(() => {
    let mounted = true;
    void getStripe()
      .then((instance) => {
        if (mounted) setStripe(instance);
      })
      .catch(() => {
        if (mounted) setStripe(null);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const setupIntentQuery = useQuery({
    ...setupIntentQueryOptions(organizationId ?? "none"),
    enabled: false,
  });
  const hasLocallyConfirmedIntent = locallyConfirmedSetupIntentId !== null;
  const isStripeConfirmed =
    setupIntentQuery.data?.status === "succeeded" ||
    setupIntentQuery.data?.setup_intent_id === locallyConfirmedSetupIntentId;

  const isDark = useDarkMode();
  const preparePayment = async (startFresh = false) => {
    if (!organizationId || setupRequestInFlight.current) return;

    setPaymentSessionError(null);
    setupRequestInFlight.current = true;
    try {
      if (startFresh) {
        resetSetupIntentFormKey(organizationId);
        await queryClient.resetQueries(
          {
            queryKey: queryKeys.billing.setupIntent(organizationId),
            exact: true,
          },
          { cancelRefetch: true },
        );
      }
      await setupIntentQuery.refetch();
    } finally {
      setupRequestInFlight.current = false;
    }
  };

  if (!organizationId) {
    return (
      <PaymentStepFrame
        onBack={onBack}
        backDisabled={hasLocallyConfirmedIntent}
      >
        <SkeletonRow count={3} />
      </PaymentStepFrame>
    );
  }

  if (!setupIntentQuery.data) {
    const hasSetupError = setupIntentQuery.isError || paymentSessionError;
    const setupErrorMessage =
      paymentSessionError ??
      (setupIntentQuery.error instanceof ApiRequestError &&
      setupIntentQuery.error.status === 503
        ? paymentErrorMessage(setupIntentQuery.error)
        : setupIntentQuery.isError
          ? "We couldn't start the payment setup. Please try again."
          : "Resume payment setup when you're ready to add your card.");
    return (
      <PaymentStepFrame
        onBack={onBack}
        backDisabled={hasLocallyConfirmedIntent}
      >
        <Alert variant={hasSetupError ? "destructive" : "default"}>
          {hasSetupError && <AlertCircle className="w-4 h-4" />}
          <AlertDescription className="flex items-center justify-between gap-4">
            <span>{setupErrorMessage}</span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => preparePayment()}
              disabled={setupIntentQuery.isFetching}
              aria-busy={setupIntentQuery.isFetching}
            >
              {setupIntentQuery.isFetching
                ? "Preparing..."
                : hasSetupError
                  ? "Retry Payment Setup"
                  : "Resume Payment Setup"}
            </Button>
          </AlertDescription>
        </Alert>
      </PaymentStepFrame>
    );
  }

  if (setupIntentQuery.data.status === "succeeded") {
    return (
      <PaymentStepFrame onBack={onBack} backDisabled>
        <FinishActivation
          organizationId={organizationId}
          setupIntentId={setupIntentQuery.data.setup_intent_id}
          onBusyChange={setIsPaymentBusy}
          onSessionError={setPaymentSessionError}
        />
      </PaymentStepFrame>
    );
  }

  if (setupIntentQuery.data.status !== "requires_payment_method") {
    const isCanceled = setupIntentQuery.data.status === "canceled";
    return (
      <PaymentStepFrame onBack={onBack} backDisabled={isPaymentBusy}>
        <Alert>
          <AlertDescription className="flex items-center justify-between gap-4">
            <span>
              {isCanceled
                ? "This payment setup was canceled. Start a new setup to add your card."
                : "Stripe is still resolving this payment setup. Refresh its status before continuing."}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => preparePayment(isCanceled)}
              disabled={setupIntentQuery.isFetching}
              aria-busy={setupIntentQuery.isFetching}
            >
              {setupIntentQuery.isFetching
                ? "Preparing..."
                : isCanceled
                  ? "Start New Payment Setup"
                  : "Refresh Payment Status"}
            </Button>
          </AlertDescription>
        </Alert>
      </PaymentStepFrame>
    );
  }

  if (stripe === null) {
    return (
      <PaymentStepFrame onBack={onBack}>
        <Alert variant="destructive">
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>
            Payments are not available in this environment.
          </AlertDescription>
        </Alert>
      </PaymentStepFrame>
    );
  }

  if (stripe === undefined) {
    return (
      <PaymentStepFrame onBack={onBack}>
        <SkeletonRow count={3} />
      </PaymentStepFrame>
    );
  }

  return (
    <PaymentStepFrame
      onBack={onBack}
      backDisabled={isPaymentBusy || isStripeConfirmed}
    >
      <Elements
        key={setupIntentQuery.data.setup_intent_id}
        stripe={stripe}
        options={{
          clientSecret: setupIntentQuery.data.client_secret,
          appearance: { theme: isDark ? "night" : "stripe" },
        }}
      >
        <CardForm
          organizationId={organizationId}
          setupIntentId={setupIntentQuery.data.setup_intent_id}
          onBusyChange={setIsPaymentBusy}
          isStripeConfirmed={isStripeConfirmed}
          onStripeConfirmedChange={(isConfirmed) =>
            setLocallyConfirmedSetupIntentId(
              isConfirmed ? setupIntentQuery.data.setup_intent_id : null,
            )
          }
          onSessionError={setPaymentSessionError}
        />
      </Elements>
    </PaymentStepFrame>
  );
};

const PaymentStepFrame = ({
  children,
  onBack,
  backDisabled = false,
}: {
  children: React.ReactNode;
  onBack: () => void;
  backDisabled?: boolean;
}) => (
  <>
    <Button
      variant="outline"
      className="w-fit"
      disabled={backDisabled}
      onClick={onBack}
    >
      <ArrowLeft className="mr-2 w-4 h-4" />
      Back
    </Button>
    <div>
      <h2 className="text-3xl font-bold tracking-tight">Payment Method</h2>
      <p className="mt-3 text-muted-foreground">
        Add the credit card your usage will be billed to. Your account activates
        as soon as the card is saved.
      </p>
    </div>
    {children}
  </>
);

const FinishActivation = ({
  organizationId,
  setupIntentId,
  onBusyChange,
  onSessionError,
}: {
  organizationId: string;
  setupIntentId: string;
  onBusyChange: (isBusy: boolean) => void;
  onSessionError: (message: string) => void;
}) => {
  const queryClient = useQueryClient();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isFinishing, setIsFinishing] = useState(false);
  const isCurrentOrganization = () =>
    useOrganizationStore.getState().organization?.id === organizationId;

  const finishActivation = async () => {
    if (!isCurrentOrganization()) return;

    setErrorMessage(null);
    setIsFinishing(true);
    onBusyChange(true);
    let activeOperation: "confirmation" | "activation" | "publication" =
      "confirmation";

    try {
      await confirmSetupIntent(organizationId, setupIntentId);
      if (!isCurrentOrganization()) return;
      activeOperation = "activation";
      const activated = await activateOrganization(organizationId);
      if (!isCurrentOrganization()) return;
      activeOperation = "publication";
      await applyActivatedOrganization(queryClient, organizationId, activated);
    } catch (err) {
      if (!isCurrentOrganization()) return;
      const message =
        activeOperation === "confirmation"
          ? paymentErrorMessage(err)
          : activationErrorMessage(err);
      setErrorMessage(message);
      onSessionError(message);
      if (
        activeOperation === "activation" &&
        hasBillingError(err, 402, "payment_method_required")
      ) {
        resetSetupIntentFormKey(organizationId);
        queryClient.removeQueries({
          queryKey: queryKeys.billing.setupIntent(organizationId),
          exact: true,
        });
      }
    } finally {
      if (isCurrentOrganization()) {
        setIsFinishing(false);
        onBusyChange(false);
      }
    }
  };

  return (
    <div className="space-y-6">
      {errorMessage && (
        <Alert variant="destructive">
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      )}
      <Alert>
        <AlertDescription>
          Your card is saved. Finish activating your organization.
        </AlertDescription>
      </Alert>
      <Button
        type="button"
        className="w-full h-12 text-base group"
        size="lg"
        onClick={finishActivation}
        disabled={isFinishing}
        aria-busy={isFinishing}
      >
        {isFinishing ? (
          <>
            <Loader2 className="mr-2 w-4 h-4 animate-spin" aria-hidden="true" />
            Finishing Activation...
          </>
        ) : (
          "Finish Activation"
        )}
        <ArrowRight
          className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1"
          aria-hidden="true"
        />
      </Button>
    </div>
  );
};

const CardForm = ({
  organizationId,
  setupIntentId,
  onBusyChange,
  isStripeConfirmed,
  onStripeConfirmedChange,
  onSessionError,
}: {
  organizationId: string;
  setupIntentId: string;
  onBusyChange: (isBusy: boolean) => void;
  isStripeConfirmed: boolean;
  onStripeConfirmedChange: (isConfirmed: boolean) => void;
  onSessionError: (message: string) => void;
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const queryClient = useQueryClient();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isConfirming, setIsConfirming] = useState(false);
  // Stripe and billing requests can settle after an organization switch remounts the form.
  const isCurrentOrganization = () =>
    useOrganizationStore.getState().organization?.id === organizationId;
  const rotatePaymentSession = async () => {
    if (!isCurrentOrganization()) return;
    onStripeConfirmedChange(false);
    resetSetupIntentFormKey(organizationId);
    queryClient.removeQueries({
      queryKey: queryKeys.billing.setupIntent(organizationId),
      exact: true,
    });
  };

  const activateMutation = useMutation({
    retry: false,
    mutationFn: () => {
      if (!isCurrentOrganization()) {
        throw new Error("Organization selection changed before activation");
      }
      return activateOrganization(organizationId);
    },
    onSuccess: (activated) => {
      if (!isCurrentOrganization()) return;
      return applyActivatedOrganization(queryClient, organizationId, activated);
    },
    onError: async (err) => {
      if (!isCurrentOrganization()) return;
      const message = activationErrorMessage(err);
      setErrorMessage(message);
      onSessionError(message);
      if (hasBillingError(err, 402, "payment_method_required")) {
        await rotatePaymentSession();
      }
    },
    onSettled: () => {
      if (isCurrentOrganization()) onBusyChange(false);
    },
  });

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!stripe || !elements || !isCurrentOrganization()) return;

    setErrorMessage(null);
    setIsConfirming(true);
    onBusyChange(true);
    let shouldActivate = false;
    let stripeWasConfirmed = isStripeConfirmed;

    try {
      if (!stripeWasConfirmed) {
        // Card-only intents never redirect, so confirmSetup resolves in place.
        const { error, setupIntent } = await stripe.confirmSetup({
          elements,
          redirect: "if_required",
        });
        if (!isCurrentOrganization()) return;

        if (error) {
          const message = stripeSetupErrorMessage(error);
          setErrorMessage(message);
          onSessionError(message);
          return;
        }
        if (setupIntent?.status !== "succeeded") {
          const message = "We couldn't save your card. Please try again.";
          setErrorMessage(message);
          onSessionError(message);
          return;
        }
        stripeWasConfirmed = true;
        onStripeConfirmedChange(true);
      }

      if (!isCurrentOrganization()) return;
      await confirmSetupIntent(organizationId, setupIntentId);
      if (!isCurrentOrganization()) return;
      shouldActivate = true;
    } catch (err) {
      if (!isCurrentOrganization()) return;
      const message = paymentErrorMessage(err);
      setErrorMessage(message);
      onSessionError(message);
      if (err instanceof ApiRequestError && err.status === 402) {
        const body = err.body as { error?: string } | null;
        if (body?.error === "payment_declined") {
          onStripeConfirmedChange(false);
        }
      }
      return;
    } finally {
      if (isCurrentOrganization()) setIsConfirming(false);
      if (!shouldActivate && isCurrentOrganization()) onBusyChange(false);
    }

    if (!isCurrentOrganization()) return;
    activateMutation.mutate();
  };

  const isBusy = isConfirming || activateMutation.isPending;

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {errorMessage && (
        <Alert variant="destructive">
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      )}

      <PaymentElement options={{ wallets: { link: "never" } }} />

      <Button
        type="submit"
        className="w-full h-12 text-base group"
        size="lg"
        disabled={!stripe || !elements || isBusy}
        aria-busy={isBusy}
      >
        {isBusy ? (
          <>
            <Loader2 className="mr-2 w-4 h-4 animate-spin" aria-hidden="true" />
            Saving Card &amp; Activating...
          </>
        ) : (
          "Save Card & Activate"
        )}
        <ArrowRight
          className="ml-2 w-4 h-4 transition-transform group-hover:translate-x-1"
          aria-hidden="true"
        />
      </Button>
    </form>
  );
};
